import clsx from 'clsx';
import { de, enUS, es, fr, pt } from 'date-fns/locale';
import { CalendarIcon, XIcon } from 'lucide-react';
import { DayPicker, Locale } from 'react-day-picker';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from '@splitfiesta/i18n';

import { getInputClasses } from '@/lib/styles/form';

import '../../styles/datepicker.css';

// Static locale map for date-fns
const DATE_FNS_LOCALES: Record<string, Locale> = {
  en: enUS,
  es: es,
  fr: fr,
  pt: pt,
  de: de,
};

interface DatePickerProps {
  id?: string;
  label?: string;
  /** Rendered inside the label, after the required marker. */
  labelAccessory?: ReactNode;
  value: string; // YYYY-MM-DD format
  onChange: (date: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  min?: string; // YYYY-MM-DD format
  max?: string; // YYYY-MM-DD format
  className?: string;
  'data-testid'?: string;
}

/**
 * Parses a YYYY-MM-DD string into a Date object (local time)
 */
function parseDate(dateString: string): Date | undefined {
  if (!dateString) return undefined;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Formats a Date object into YYYY-MM-DD string
 */
function formatDateToString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function DatePicker({
  id,
  label,
  labelAccessory,
  value,
  onChange,
  placeholder,
  disabled = false,
  required = false,
  min,
  max,
  className = '',
  'data-testid': testId,
}: DatePickerProps) {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Get date-fns locale based on current language
  const locale = useMemo(() => {
    const language = i18n?.language || 'en';
    return DATE_FNS_LOCALES[language] || DATE_FNS_LOCALES.en;
  }, [i18n?.language]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  const selectedDate = parseDate(value);
  const minDate = parseDate(min || '');
  const maxDate = parseDate(max || '');

  const handleSelect = useCallback(
    (date: Date | undefined) => {
      if (date) {
        onChange(formatDateToString(date));
      }
      setIsOpen(false);
    },
    [onChange]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange('');
    },
    [onChange]
  );

  const displayValue = selectedDate
    ? selectedDate.toLocaleDateString(i18n?.language || 'en', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '';

  return (
    <div className={clsx('relative', className)} ref={containerRef}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
          {labelAccessory}
        </label>
      )}

      <button
        ref={buttonRef}
        id={id}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={clsx(
          getInputClasses({ disabled }),
          'relative flex items-center justify-between pr-10 cursor-pointer text-left'
        )}
        data-testid={testId}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span
          className={clsx(
            'truncate',
            !displayValue && 'text-gray-400 dark:text-gray-500'
          )}
        >
          {displayValue || placeholder || t('common:placeholders.selectDate')}
        </span>
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-0.5">
          {value && !disabled && !required && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={e =>
                e.key === 'Enter' &&
                handleClear(e as unknown as React.MouseEvent)
              }
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
              aria-label={t('common:actions.clear')}
            >
              <XIcon className="w-4 h-4 text-gray-400" />
            </span>
          )}
          <CalendarIcon className="w-5 h-5 text-gray-400 pointer-events-none" />
        </div>
      </button>

      {isOpen && (
        <div
          className="date-picker-popover absolute top-full left-0 mt-1"
          role="dialog"
          aria-modal="true"
          aria-label={t('common:labels.calendar')}
        >
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            locale={locale}
            disabled={[
              ...(minDate ? [{ before: minDate }] : []),
              ...(maxDate ? [{ after: maxDate }] : []),
            ]}
            defaultMonth={selectedDate || new Date()}
            showOutsideDays
            fixedWeeks
          />
        </div>
      )}
    </div>
  );
}

export default DatePicker;
